import type { HubChromeOpportunityRow, HubChromeScriptRow } from "./hub-chrome.types.js";

/** Rows the hub can fan out: anything that knows its own id and owning set. */
export interface HubSetScopedRow {
  id: string;
  setId: string;
}

export interface HubSetGroup<T extends HubSetScopedRow> {
  setId: string;
  ids: string[];
  rows: T[];
}

/**
 * Splits a cross-set selection into per-set groups, because the persona script
 * endpoints are set-scoped: generate and ai-edit 404 on a foreign id, and
 * delete silently skips it.
 *
 * Groups follow first-seen row order so the resulting jobs are deterministic.
 */
export function groupBySet<T extends HubSetScopedRow>(
  rows: readonly T[],
  selectedIds: readonly string[] | ReadonlySet<string>,
): HubSetGroup<T>[] {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  if (selected.size === 0) return [];

  const groups = new Map<string, HubSetGroup<T>>();
  const seen = new Set<string>();

  for (const row of rows) {
    if (!selected.has(row.id) || seen.has(row.id)) continue;
    seen.add(row.id);

    const group = groups.get(row.setId);
    if (group) {
      group.ids.push(row.id);
      group.rows.push(row);
      continue;
    }
    groups.set(row.setId, { setId: row.setId, ids: [row.id], rows: [row] });
  }

  return [...groups.values()];
}

export interface HubScriptSelection {
  scripts: HubChromeScriptRow[];
  briefs: HubChromeScriptRow[];
}

/**
 * Scripts and briefs share one hub list but have separate endpoints, so every
 * script action has to split the selection before grouping it.
 */
export function splitByKind(
  rows: readonly HubChromeScriptRow[],
  selectedIds: readonly string[] | ReadonlySet<string>,
): HubScriptSelection {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const scripts: HubChromeScriptRow[] = [];
  const briefs: HubChromeScriptRow[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    if (!selected.has(row.id) || seen.has(row.id)) continue;
    seen.add(row.id);
    if (row.kind === "brief") briefs.push(row);
    else scripts.push(row);
  }

  return { scripts, briefs };
}

/**
 * Keeps a snapshot of every selected row, including ones the current filter or
 * search has dropped from `rows`. The hub pill counts selected ids; generate
 * has to use the same set, not only the rows still on screen.
 */
export function rememberSelectedRows<T extends { id: string }>(
  cache: Map<string, T>,
  rows: readonly T[],
  selectedIds: readonly string[],
): T[] {
  const selected = new Set(selectedIds);
  for (const row of rows) {
    if (selected.has(row.id)) cache.set(row.id, row);
  }
  for (const id of cache.keys()) {
    if (!selected.has(id)) cache.delete(id);
  }
  return selectedIds.flatMap((id) => {
    const row = cache.get(id);
    return row ? [row] : [];
  });
}

/** Largest per-set group size — the AI Edit cap applies per job, not per selection. */
export function maxGroupSize<T extends HubSetScopedRow>(groups: readonly HubSetGroup<T>[]): number {
  return groups.reduce((max, group) => Math.max(max, group.ids.length), 0);
}

/** Post-Flight rows are not bulk-selectable on the hub (no generate-scripts bulk action). */
export function hubOpportunitySelectionEnabled(row: HubChromeOpportunityRow): boolean {
  return row.setType !== "POST_FLIGHT";
}

/** Post-Flight opportunities use double-down flows, not the hub generate modal. */
export function hubSelectionAllowsScriptGeneration(
  rows: readonly HubChromeOpportunityRow[],
  selectedIds: readonly string[],
): boolean {
  if (selectedIds.length === 0) return false;
  const byId = new Map(rows.map((row) => [row.id, row]));
  return selectedIds.every((id) => {
    const row = byId.get(id);
    return row != null && hubOpportunitySelectionEnabled(row);
  });
}
